import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  DeveloperListDto,
  DeveloperListFilter,
  DeveloperListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { DeveloperFilter } from "../DeveloperFilter";
import { developerPageRoute } from "../DeveloperPage/DeveloperPage";
import { DeveloperTable } from "./DeveloperTable";

const routeName = "developers";

export const developerListPageRoute: PageRoute = {
  handle: "Developers",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const DeveloperListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Developer;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<DeveloperListFilter>({
    sortObjects: [{ property: pn<DeveloperListDto>("id"), isDesc: true }],
  });
  const [initListFilter] = useState<DeveloperListFilter>(listFilter);
  const [listItems, setListItems] = useState<DeveloperListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<DeveloperListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.developers.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.developers.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    fetchResource();
    loadList(listFilter);
  }, []);

  const fetchResource = () => {
    ApiService.developers.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<DeveloperListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <DeveloperFilter
        filter={listFilter.developerFilter || {}}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.developerFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.developers.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{developerListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() => navigate(developerPageRoute.getPath({ id: 0, crudMode: "edit" }))}
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />

              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <DeveloperTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              developers={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
